/**
 * Blog Listing Page
 * Display all published property news blog posts
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import BlogCard from '@/components/BlogCard';
import { ChevronLeft, ChevronRight, Loader2, Calendar, Sparkles } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { trackBlogListView, trackBlogPagination, trackBlogCardClick } from '@/services/analytics';
import { AdUnit } from '@/components/AdUnit';

const POSTS_PER_PAGE = 9;

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  summary: string;
  content: string;
  imageUrl: string;
  publishedAt: string;
  date: string; // Daily post date
  keywords: string[];
  sources: Array<{
    title: string;
    url: string;
    source: string;
  }>;
}

export default function Blog() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // SEO
  usePageMeta({
    title: "Daily Property News | Let's Be Real",
    description: "Brutally honest Australian property news, delivered daily. Get today's market updates, interest rate analysis, and real estate insights with personality.",
    canonical: "https://letsbereal.com.au/blog",
    ogImage: "https://letsbereal.com.au/lbr.png",
    ogType: "website",
    twitterCard: "summary_large_image",
    keywords: "australian property news, real estate news, property market updates, housing market, interest rates, property investment, daily property news, property blog",
    author: "Let's Be Real",
    schema: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Let's Be Real Property News",
      "description": "Daily Australian property market news and analysis",
      "url": "https://letsbereal.com.au/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Let's Be Real",
        "logo": {
          "@type": "ImageObject",
          "url": "https://letsbereal.com.au/lbr.png"
        }
      },
      "inLanguage": "en-AU"
    },
  });

  // Fetch published blog posts
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/blog/published');
        const data = await response.json();
        
        if (data.success) {
          setBlogPosts(data.data);
          // Track blog list view
          trackBlogListView(data.data.length);
        } else {
          setError('Failed to load blog posts');
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError('Error loading blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const totalPages = Math.ceil(blogPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = blogPosts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Track pagination
    trackBlogPagination(page, totalPages);
  };

  // Get featured post (first post on first page)
  const featuredPost = currentPage === 1 && blogPosts.length > 0 ? blogPosts[0] : null;
  const regularPosts = currentPage === 1 ? currentPosts.slice(1) : currentPosts;

  return (
    <div className="min-h-screen bg-background">
      {/* Enhanced Hero Section */}
      <div className="border-b bg-gradient-to-r from-primary/5 via-transparent to-primary/5">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 lg:py-14">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="mb-4">
              <Sparkles className="h-3 w-3 mr-1" /> Updated Daily
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
              Property News
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Your daily dose of Aussie property drama. We read the boring stuff so you don't have to. 🏠
            </p>
            {blogPosts.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-sm text-muted-foreground">
                <span>{blogPosts.length} articles</span>
                <span className="hidden sm:inline">•</span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Last updated: {formatDate(blogPosts[0]?.publishedAt)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Banner Ad (Desktop) - After AdSense Approval */}
      <div className="container mx-auto px-4 sm:px-6 py-4">
        <AdUnit 
          adSlot="blog-listing-top" 
          adFormat="leaderboard" 
          className="hidden md:flex justify-center"
        />
      </div>

      {/* Blog Posts Grid */}
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-8 sm:py-12 text-center px-4">
              <h2 className="text-lg sm:text-xl font-semibold mb-2">Oops! Something went wrong</h2>
              <p className="text-sm sm:text-base text-muted-foreground">{error}</p>
            </CardContent>
          </Card>
        ) : blogPosts.length === 0 ? (
          <Card>
            <CardContent className="py-8 sm:py-12 text-center px-4">
              <h2 className="text-lg sm:text-xl font-semibold mb-2">Nothing here yet...</h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                We're cooking up some spicy property news. Check back next week! 🔥
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Featured Post (First page only) */}
            {featuredPost && (
              <Card 
                className="mb-8 overflow-hidden hover:shadow-xl transition-all cursor-pointer group border-2 hover:border-primary/50"
                onClick={() => {
                  trackBlogCardClick({ postId: featuredPost.id, title: featuredPost.title, position: 1 });
                  navigate(`/blog/${featuredPost.slug}`);
                }}
              >
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="aspect-video md:aspect-auto overflow-hidden">
                    <img
                      src={featuredPost.imageUrl}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 sm:p-8 flex flex-col justify-center">
                    <Badge variant="default" className="w-fit mb-3">Latest</Badge>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                      {featuredPost.title}
                    </h2>
                    <p className="text-muted-foreground mb-4 line-clamp-3">
                      {featuredPost.summary}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {formatDate(featuredPost.publishedAt)}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Regular Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {regularPosts.map((blog, index) => (
                <BlogCard 
                  key={blog.id} 
                  blog={blog} 
                  position={startIndex + index + (featuredPost ? 2 : 1)}
                />
              ))}
            </div>

            {/* Mid-Content Ad (Responsive) - After AdSense Approval */}
            <div className="my-8">
              <AdUnit 
                adSlot="blog-listing-mid" 
                adFormat="auto" 
                fullWidthResponsive 
                className="flex justify-center"
              />
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="space-y-4">
                <p className="text-center text-sm text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(endIndex, blogPosts.length)} of {blogPosts.length} articles
                </p>
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="h-8 w-8 sm:h-10 sm:w-10"
                  >
                    <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="icon"
                        onClick={() => handlePageChange(page)}
                        className="h-8 w-8 sm:h-10 sm:w-10 text-xs sm:text-sm"
                      >
                        {page}
                      </Button>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 sm:h-10 sm:w-10"
                  >
                    <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

